const durationCache = new Map();
const metadataCache = new Map();

const readVideoMetadata = ({ url, crossOrigin }) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    if (crossOrigin) {
      video.crossOrigin = crossOrigin;
    }

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = "";
    };

    video.onloadedmetadata = () => {
      const width = Number(video.videoWidth);
      const height = Number(video.videoHeight);
      const metadata = {
        duration: Number.isFinite(video.duration) ? video.duration : null,
        width: width > 0 ? width : null,
        height: height > 0 ? height : null,
      };
      cleanup();
      resolve(metadata);
    };

    video.onerror = () => {
      cleanup();
      resolve(null);
    };

    video.src = url;
  });

export const getVideoMetadataAsync = async ({ url }) => {
  if (!url) {
    return null;
  }

  if (metadataCache.has(url)) {
    return metadataCache.get(url);
  }

  let metadata = await readVideoMetadata({ url, crossOrigin: "anonymous" });
  if (!metadata?.width || !metadata?.height) {
    metadata = await readVideoMetadata({ url });
  }

  metadataCache.set(url, metadata);
  if (metadata?.duration != null) {
    durationCache.set(url, metadata.duration);
  }
  return metadata;
};

export const getVideoDurationAsync = ({ url }) => {
  if (!url) {
    return Promise.resolve(null);
  }

  if (durationCache.has(url)) {
    return Promise.resolve(durationCache.get(url));
  }

  return getVideoMetadataAsync({ url }).then((metadata) => metadata?.duration ?? null);
};

const imageMetadataCache = new Map();

export const getImageMetadataAsync = ({ url }) => {
  if (!url) {
    return Promise.resolve(null);
  }

  if (imageMetadataCache.has(url)) {
    return Promise.resolve(imageMetadataCache.get(url));
  }

  return new Promise((resolve) => {
    const image = new Image();

    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      image.src = "";
    };

    image.onload = () => {
      const metadata = {
        width: image.naturalWidth || null,
        height: image.naturalHeight || null,
      };
      imageMetadataCache.set(url, metadata);
      cleanup();
      resolve(metadata);
    };

    image.onerror = () => {
      imageMetadataCache.set(url, null);
      cleanup();
      resolve(null);
    };

    image.src = url;
  });
};

export const isVideoDurationInRange = ({
  duration,
  minSeconds,
  maxSeconds,
}) => {
  if (duration == null || !Number.isFinite(duration)) {
    return false;
  }

  return duration >= minSeconds && duration <= maxSeconds;
};
